import { TrackedLinkContext } from "@faire/deprecated-retailer-visitor-shared/components/TrackedLink/index";
import { ProductPositioningContext } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/productTileContext";
import { SaveToBoardProvider } from "@faire/deprecated-retailer-visitor-shared/features/RetailerBoards/Context";
import { ProductTileClickImpressionProvider } from "@faire/deprecated-retailer-visitor-shared/hooks/ProductTileImpression/ProductTileClickImpressionProvider";
import { ProductTileViewImpressionProvider } from "@faire/deprecated-retailer-visitor-shared/hooks/ProductTileImpression/ProductTileViewImpressionProvider";
import { MaybeServerRenderedProductTileWrapper } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/util/MaybeServerRenderedProductTileWrapper";
import { trackSearchIrrelevanceCarouselScroll } from "@faire/web-api--source/events/search/scroll/irrelevanceCarousel";
import { trackSearchIrrelevanceCarouselView } from "@faire/web-api--source/events/search/view/irrelevanceCarousel";
import { Surface } from "@faire/web-api--source/faire/logging/data/Surface";
import { IClickInfo } from "@faire/web-api--source/indigofair/data/IClickInfo";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import { ProductTileContainer } from "@faire/web-api--source/indigofair/data/ProductTileContainer";
import { ProductTileScreenContainer } from "@faire/web-api--source/indigofair/data/ProductTileScreenContainer";
import { useTraceComponentRender } from "@faire/web/common/tracing/useTraceComponentRender";
import { HorizontalLine } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { ResponsiveValues } from "@faire/web/slate/Theme/ResponsiveValues";
import { Typography } from "@faire/web/slate/Typography";
import { Carousel } from "@faire/web/ui-labs/Carousel/Carousel";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";
import styled, { css } from "styled-components";

interface SearchRelevanceCarouselProps {
  products: ILeanProductTile[];
  brandMap?: Record<string, ILeanProductTile.IBrand | undefined>;
  itemsPerSlideResponsive: ResponsiveValues<number>;
  handleRightClick: (product?: ILeanProductTile.ILeanProduct) => () => void;
  requestId?: string;
  queryString: string;
  containerName: string;
  productTileScreen: Surface;
  insertionIndex: number;
}

const BATCH_SIZE = 2;

export const SearchRelevanceCarousel: React.FC<
  SearchRelevanceCarouselProps
> = ({
  products,
  brandMap,
  itemsPerSlideResponsive,
  handleRightClick,
  requestId,
  queryString,
  containerName,
  productTileScreen,
  insertionIndex,
}) => {
  useTraceComponentRender("SearchRelevanceCarousel");

  const itemsPerRow = useResponsiveValue(itemsPerSlideResponsive);

  const context =
    `${ProductTileContainer.SEARCH_PRODUCT_RESULTS_IRRELEVANCE_CAROUSEL}|${queryString}` as const;
  if (products.length === 0) {
    return null;
  }
  const renderIrrelevantProduct = (
    productTile: ILeanProductTile,
    index?: number
  ) => {
    return (
      <TileWrapper onContextMenu={handleRightClick(productTile?.product)}>
        <ProductTileViewImpressionProvider
          context={context}
          requestId={requestId}
          screenContext={productTileScreen}
          containerContext={
            ProductTileScreenContainer.SEARCH_PRODUCT_RESULTS_IRRELEVANCE_PRODUCTS
          }
          contextToken={queryString ?? ""}
        >
          <ProductTileClickImpressionProvider
            context={context}
            productTileContainer={
              ProductTileContainer.SEARCH_PRODUCT_RESULTS_IRRELEVANCE_CAROUSEL
            }
            requestId={requestId}
            containerName={containerName}
            screenContext={productTileScreen}
            containerContext={
              ProductTileScreenContainer.SEARCH_PRODUCT_RESULTS_IRRELEVANCE_PRODUCTS
            }
            contextToken={queryString ?? ""}
          >
            <TrackedLinkContext.Provider
              value={{
                type: IClickInfo.Type.PRODUCT,
                containerName,
                requestId: requestId,
              }}
            >
              <SaveToBoardProvider value={{ trackOrigin: "search" }}>
                <ProductPositioningContext.Provider
                  value={{ index, itemsPerRow }}
                >
                  <MaybeServerRenderedProductTileWrapper
                    index={index ?? 0}
                    productTile={productTile}
                    brand={brandMap?.[productTile.product?.brand_token ?? ""]}
                    requestId={requestId}
                    containerName={containerName}
                    context={context}
                    productTileContainer={
                      ProductTileContainer.SEARCH_PRODUCT_RESULTS_IRRELEVANCE_CAROUSEL
                    }
                    contextToken={queryString ?? ""}
                    containerContext={
                      ProductTileScreenContainer.SEARCH_PRODUCT_RESULTS_IRRELEVANCE_PRODUCTS
                    }
                    screenContext={productTileScreen}
                    productTokens={undefined}
                    isIrrelevantProductTile
                  />
                </ProductPositioningContext.Provider>
              </SaveToBoardProvider>
            </TrackedLinkContext.Provider>
          </ProductTileClickImpressionProvider>
        </ProductTileViewImpressionProvider>
      </TileWrapper>
    );
  };

  return (
    <CarouselWrapper
      $columns={itemsPerSlideResponsive}
      data-test-id="search-relevance-carousel"
    >
      <Spacer size="0" className="pt-2 lg:pt-6" />
      <HorizontalLine />
      <Spacer size="0" className="pt-8 lg:pt-10" />
      <Typography variant="pageHeaderSerifRegular">
        <StrictLocalMsg
          id="QihnfP"
          defaultMessage="Other products you might like"
          description={{
            text: "Carousel header for a list of products that might be a good fit for the retailer",
            img: "https://cdn.faire.com/fastly/6c806c11aa1b9dc0ba378b21f6b9ffc5ca56efc28fa747f5833e7b7983064ce8.png",
          }}
        />
      </Typography>
      <Spacer size="3x" />
      <StyledCarousel
        items={products}
        itemGap={8}
        renderItem={renderIrrelevantProduct}
        itemsPerSlide={itemsPerSlideResponsive}
        loop={false}
        peekNextPercent={{
          mobileAndAbove: 10,
          tabletAndAbove: 10,
          desktopAndAbove: 0,
        }}
        onVisible={(items: ILeanProductTile[]) => {
          const isFirstSlide = items.length > 0 && items[0] === products[0];
          if (isFirstSlide) {
            trackSearchIrrelevanceCarouselView(
              requestId ?? "",
              // This calculates the number of products shown before the carousel
              ((insertionIndex + 1) * itemsPerRow * BATCH_SIZE + 1).toString(),
              itemsPerRow.toString()
            );
          } else {
            trackSearchIrrelevanceCarouselScroll(requestId ?? "");
          }
        }}
        noMobilePadding
      />
      <Spacer size="0" className="pt-8 lg:pt-10" />
      <HorizontalLine />
      <Spacer size="0" className="pt-8 lg:pt-12" />
      <Typography variant="pageHeaderSerifRegular">
        <StrictLocalMsg
          id="R2VTUi"
          defaultMessage='More results for "{searchToken}"'
          description={{
            text: "This header is shown on the product search page for the second section of search results.",
            img: "https://cdn.faire.com/fastly/8ce06f3be3cc583bbb03c1e09ec09e4b03071d932fcddbfe55b94b04155d1333.png",
          }}
          values={{
            searchToken: queryString,
          }}
        />
      </Typography>
    </CarouselWrapper>
  );
};

const StyledCarousel = styled(Carousel<ILeanProductTile>)`
  width: 100%;
`;

const TileWrapper = styled.div`
  width: 100%;
`;

const CarouselWrapper = styled.div<{
  $columns: ResponsiveValues<number>;
}>`
  padding-top: 0px;
  padding-bottom: 0px;
  ${responsiveMediaQueries.mobile} {
    grid-column: ${({ $columns }) => css`1/ span ${$columns.mobileAndAbove}`};
  }
  ${responsiveMediaQueries.tablet} {
    grid-column: ${({ $columns }) => css`1/ span ${$columns.tabletAndAbove}`};
  }
  ${responsiveMediaQueries.desktop} {
    grid-column: ${({ $columns }) => css`1/ span ${$columns.desktopAndAbove}`};
  }
  ${responsiveMediaQueries.xlarge} {
    grid-column: ${({ $columns }) => css`1/ span ${$columns.xLargeAndAbove}`};
  }
  ${responsiveMediaQueries.xxlarge} {
    grid-column: ${({ $columns }) => css`1/ span ${$columns.xxLargeDesktop}`};
  }
`;
